import React, { useState, useRef, useEffect } from 'react';
import { 
  Image, Video, Upload, Crown, ArrowLeft, ArrowUp, ArrowDown,
  X, Loader2, Link as LinkIcon, Clock, Trash2, Plus, Shuffle, MousePointerClick
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import NavigationMenu from '../components/NavigationMenu';
import DynamicFooter from '../components/DynamicFooter';
import BackgroundClicksChart from '../components/BackgroundClicksChart';
import BackgroundPreviewSlideshow from '../components/BackgroundPreviewSlideshow';
import axios from 'axios';

const BACKEND_URL_BG = process.env.REACT_APP_BACKEND_URL;

const LinkEditor = ({ bgId, initialLink, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(initialLink || '');
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [editing]);

  const handleSave = () => {
    setEditing(false);
    if (value !== (initialLink || '')) {
      onSave(bgId, 'link', value);
    }
  };

  if (!editing) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-sm text-blue-300/70 truncate flex-1" data-testid={`bg-link-display-${bgId}`}>
          {initialLink || <span className="text-blue-300/30 italic">Aucun lien</span>}
        </span>
        <button
          onClick={() => { setValue(initialLink || ''); setEditing(true); }}
          className="text-xs px-2 py-1 rounded-md bg-blue-500/10 text-blue-300 hover:bg-blue-500/20 border border-blue-500/20 transition-colors whitespace-nowrap"
          data-testid={`bg-link-edit-btn-${bgId}`}
        >
          Modifier
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') setEditing(false); }}
        placeholder="https://..."
        data-testid={`bg-link-input-${bgId}`}
        className="flex-1 bg-slate-900/50 border border-blue-400/40 rounded-lg px-3 py-1.5 text-white text-sm placeholder-blue-300/30 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400/30"
      />
      <button
        onClick={handleSave}
        className="text-xs px-2 py-1 rounded-md bg-green-500/20 text-green-300 hover:bg-green-500/30 border border-green-500/30 transition-colors whitespace-nowrap"
        data-testid={`bg-link-save-btn-${bgId}`}
      >
        OK
      </button>
      <button
        onClick={() => setEditing(false)}
        className="text-xs px-1.5 py-1 rounded-md text-red-300/60 hover:text-red-300 hover:bg-red-500/10 transition-colors"
        data-testid={`bg-link-cancel-btn-${bgId}`}
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

const BackgroundItem = ({ bg, index, total, onUpdate, onDelete, onMove }) => {
  const linkRef = useRef(null);

  // Sync server value only when input is NOT focused
  useEffect(() => {
    if (linkRef.current && linkRef.current !== document.activeElement) {
      linkRef.current.value = bg.link || '';
    }
  }, [bg.link]);

  const handleLinkBlur = () => {
    const val = linkRef.current?.value || '';
    if (val !== (bg.link || '')) {
      onUpdate(bg.id, 'link', val);
    }
  };

  return (
    <div className="bg-slate-800/50 border border-blue-500/20 rounded-xl p-4 flex gap-4" data-testid={`bg-item-${bg.id}`}>
      <div className="w-40 h-24 rounded-lg overflow-hidden flex-shrink-0 bg-slate-900 relative">
        {bg.type === 'video' ? (
          <video src={`${BACKEND_URL_BG}${bg.url}`} className="w-full h-full object-cover" muted />
        ) : (
          <img src={`${BACKEND_URL_BG}${bg.url}`} className="w-full h-full object-cover" alt={`Fond ${index + 1}`} />
        )}
        <div className="absolute top-1 left-1 bg-black/60 text-white text-xs px-2 py-0.5 rounded-full">
          {bg.type === 'video' ? <Video className="w-3 h-3 inline mr-1" /> : <Image className="w-3 h-3 inline mr-1" />}
          #{index + 1}
        </div>
        {bg.link && (
          <div className="absolute bottom-1 right-1 bg-black/60 text-blue-300 text-xs px-2 py-0.5 rounded-full flex items-center gap-1" data-testid={`bg-clicks-${bg.id}`}>
            <MousePointerClick className="w-3 h-3" />
            {bg.clicks || 0}
          </div>
        )}
      </div>
      <div className="flex-1 space-y-2">
        <div className="flex gap-3">
          <div className="flex-1">
            <label className="text-xs text-blue-300/60 flex items-center gap-1">
              <Clock className="w-3 h-3" /> Durée (secondes)
            </label>
            <input
              type="number"
              min="3"
              max="120"
              value={bg.duration || 10}
              onChange={(e) => onUpdate(bg.id, 'duration', parseInt(e.target.value) || 10)}
              className="w-24 bg-slate-900/50 border border-blue-500/20 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-blue-400"
            />
          </div>
          <div className="flex-1">
            <label className="text-xs text-blue-300/60 flex items-center gap-1">
              <LinkIcon className="w-3 h-3" /> Lien cliquable
            </label>
            <input
              ref={linkRef}
              type="url"
              defaultValue={bg.link || ''}
              onBlur={handleLinkBlur}
              onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
              placeholder="https://..."
              data-testid={`bg-link-input-${bg.id}`}
              className="w-full bg-slate-900/50 border border-blue-500/20 rounded-lg px-3 py-1.5 text-white text-sm placeholder-blue-300/30 focus:outline-none focus:border-blue-400"
            />
          </div>
        </div>
        <p className="text-xs text-blue-300/40 truncate">{bg.filename || bg.url.split('/').pop()}</p>
      </div>
      <div className="flex flex-col gap-1 justify-center">
        <button
          onClick={() => onMove(bg.id, 'up')}
          disabled={index === 0}
          className={`p-1.5 rounded-lg transition-colors ${index === 0 ? 'text-blue-300/20' : 'text-blue-300/60 hover:bg-blue-500/20 hover:text-blue-300'}`}
        >
          <ArrowUp className="w-4 h-4" />
        </button>
        <button
          onClick={() => onMove(bg.id, 'down')}
          disabled={index === total - 1}
          className={`p-1.5 rounded-lg transition-colors ${index === total - 1 ? 'text-blue-300/20' : 'text-blue-300/60 hover:bg-blue-500/20 hover:text-blue-300'}`}
        >
          <ArrowDown className="w-4 h-4" />
        </button>
        <button
          onClick={() => onDelete(bg.id)}
          className="p-1.5 rounded-lg text-red-400/60 hover:bg-red-500/20 hover:text-red-400 transition-colors"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

const API = `${BACKEND_URL_BG}/api`;

const UserBackgroundManager = () => {
  const { user, token, isAuthenticated, isPro, loading } = useAuth();
  const navigate = useNavigate();
  const [backgrounds, setBackgrounds] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [newDuration, setNewDuration] = useState(10);
  const [newLink, setNewLink] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [saving, setSaving] = useState({});
  const [loadingBgs, setLoadingBgs] = useState(true);
  const [randomOrder, setRandomOrder] = useState(false);
  const [savingRandomOrder, setSavingRandomOrder] = useState(false);
  const fileInputRef = useRef(null);
  const { toast } = useToast();

  const userIdRef = useRef(null);

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/');
      return;
    }
    if (user?.id && user.id !== userIdRef.current) {
      userIdRef.current = user.id;
      loadBackgrounds();
    }
  }, [loading, isAuthenticated, user?.id]);

  const loadBackgrounds = async () => {
    try {
      const res = await axios.get(`${API}/user/${user.id}/backgrounds`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBackgrounds(res.data.backgrounds || []);
      setRandomOrder(res.data.random_order || false);
    } catch (error) {
      console.error('Error loading backgrounds:', error);
    } finally {
      setLoadingBgs(false);
    }
  };

  const toggleRandomOrder = async () => {
    setSavingRandomOrder(true);
    try {
      const newValue = !randomOrder;
      await axios.put(`${API}/user/${user.id}/backgrounds-settings`, {
        random_order: newValue
      }, { headers: { Authorization: `Bearer ${token}` } });
      setRandomOrder(newValue);
      toast({ title: newValue ? 'Ordre aléatoire activé' : 'Ordre séquentiel activé' });
    } catch (error) {
      toast({ title: 'Erreur', description: 'Impossible de modifier le paramètre', variant: 'destructive' });
    } finally {
      setSavingRandomOrder(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
      toast({ title: 'Type non supporté', description: 'Images et vidéos uniquement', variant: 'destructive' });
      return;
    }
    if (file.size > 100 * 1024 * 1024) {
      toast({ title: 'Fichier trop volumineux', description: 'Maximum 100 Mo', variant: 'destructive' });
      return;
    }
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const uploadBackground = async () => {
    if (!selectedFile || !user?.id) return;
    setUploading(true);
    setUploadProgress(0);

    try {
      const headers = { Authorization: `Bearer ${token}` };
      const initRes = await axios.post(`${API}/user/${user.id}/background/initiate`, {
        filename: selectedFile.name,
        file_size: selectedFile.size,
        mime_type: selectedFile.type
      }, { headers });

      const { upload_id, chunk_size } = initRes.data;
      let offset = 0;
      let chunkIndex = 0;

      while (offset < selectedFile.size) {
        const chunk = selectedFile.slice(offset, offset + chunk_size);
        const chunkData = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target.result.split(',')[1]);
          reader.readAsDataURL(chunk);
        });
        await axios.post(`${API}/user/background/chunk`, {
          upload_id, chunk_index: chunkIndex, chunk_data: chunkData
        }, { headers });
        offset += chunk_size;
        chunkIndex++;
        setUploadProgress(Math.min(90, (offset / selectedFile.size) * 100));
      }

      await axios.post(`${API}/user/background/complete`, {
        upload_id,
        duration: newDuration,
        link: newLink
      }, { headers });

      toast({ title: 'Fond ajouté !', description: 'Votre fond a été enregistré avec succès' });
      setSelectedFile(null);
      setPreviewUrl(null);
      setNewDuration(10);
      setNewLink('');
      setUploadProgress(100);
      loadBackgrounds();
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || "Impossible d'uploader le fond",
        variant: 'destructive'
      });
    } finally {
      setUploading(false);
    }
  };

  const updateBackground = async (bgId, field, value) => {
    setSaving(prev => ({ ...prev, [bgId]: true }));
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.put(`${API}/user/${user.id}/backgrounds/${bgId}`, {
        [field]: value
      }, { headers });
      setBackgrounds(res.data.backgrounds);
      toast({ title: 'Mis à jour' });
    } catch (error) {
      toast({ title: 'Erreur', description: 'Impossible de mettre à jour', variant: 'destructive' });
    } finally {
      setSaving(prev => ({ ...prev, [bgId]: false }));
    }
  };

  const deleteBackground = async (bgId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.delete(`${API}/user/${user.id}/backgrounds/${bgId}`, { headers });
      setBackgrounds(res.data.backgrounds);
      toast({ title: 'Fond supprimé' });
    } catch (error) {
      toast({ title: 'Erreur', description: 'Impossible de supprimer', variant: 'destructive' });
    }
  };

  const moveBackground = async (bgId, direction) => {
    const idx = backgrounds.findIndex(bg => bg.id === bgId);
    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= backgrounds.length) return;
    
    const headers = { Authorization: `Bearer ${token}` };
    // Swap orders
    await axios.put(`${API}/user/${user.id}/backgrounds/${backgrounds[idx].id}`, {
      order: newIdx
    }, { headers });
    await axios.put(`${API}/user/${user.id}/backgrounds/${backgrounds[newIdx].id}`, {
      order: idx
    }, { headers });
    loadBackgrounds();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900">
      <Toaster />
      <NavigationMenu />
      
      <main className="pt-24 pb-12 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <Link to="/profile" className="text-blue-400 hover:text-blue-300">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-white">Fonds personnalisés</h1>
              <p className="text-blue-300/70 text-sm">
                Gérez vos fonds d'écran pour les pages de téléchargement ({backgrounds.length}/5)
              </p>
            </div>
          </div>

          {!isPro ? (
            <div className="bg-slate-800/50 border border-yellow-500/20 rounded-xl p-8 text-center">
              <Crown className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-white mb-2">Fonctionnalité Pro</h2>
              <p className="text-blue-300/60 mb-4">
                Passez en version Pro pour personnaliser vos pages de téléchargement.
              </p>
              <Link to="/pricing">
                <Button className="bg-gradient-to-r from-yellow-500 to-amber-500 text-white font-semibold">
                  Voir les tarifs
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Existing backgrounds gallery */}
              {loadingBgs ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 text-blue-400 animate-spin" />
                </div>
              ) : backgrounds.length > 0 ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-white">Séquence des fonds</h2>
                    <button
                      onClick={toggleRandomOrder}
                      disabled={savingRandomOrder}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                        randomOrder
                          ? 'bg-blue-500/20 text-blue-300 border border-blue-500/40'
                          : 'bg-slate-700/50 text-blue-300/50 border border-blue-500/10 hover:border-blue-500/30'
                      }`}
                      data-testid="random-order-toggle"
                    >
                      {savingRandomOrder ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shuffle className="w-4 h-4" />}
                      Ordre aléatoire
                    </button>
                  </div>
                  {/* Stats summary */}
                  {backgrounds.some(bg => (bg.clicks || 0) > 0) && (
                    <div className="flex items-center gap-4 bg-slate-800/30 border border-blue-500/10 rounded-xl px-4 py-3">
                      <MousePointerClick className="w-5 h-5 text-blue-400" />
                      <div>
                        <p className="text-sm text-white font-medium">{backgrounds.reduce((sum, bg) => sum + (bg.clicks || 0), 0)} clics au total</p>
                        <p className="text-xs text-blue-300/50">sur {backgrounds.filter(bg => bg.link).length} fond(s) avec lien</p>
                      </div>
                    </div>
                  )}
                  {/* Click Stats Chart */}
                  <BackgroundClicksChart userId={user?.id} token={token} backgrounds={backgrounds} />
                  {/* Cross-fade Preview */}
                  {backgrounds.length >= 2 && (
                    <BackgroundPreviewSlideshow backgrounds={backgrounds} />
                  )}
                  {backgrounds.map((bg, i) => (
                    <BackgroundItem
                      key={bg.id}
                      bg={bg}
                      index={i}
                      total={backgrounds.length}
                      onUpdate={updateBackground}
                      onDelete={deleteBackground}
                      onMove={moveBackground}
                    />
                  ))}
                </div>
              ) : (
                <div className="bg-slate-800/30 border border-dashed border-blue-500/20 rounded-xl p-8 text-center">
                  <Image className="w-10 h-10 text-blue-300/30 mx-auto mb-3" />
                  <p className="text-blue-300/50 text-sm">Aucun fond configuré</p>
                  <p className="text-blue-300/30 text-xs mt-1">Ajoutez jusqu'à 5 images ou vidéos</p>
                </div>
              )}

              {/* Add new background */}
              {backgrounds.length < 5 && (
                <div className="bg-slate-800/50 border border-blue-500/20 rounded-xl p-6">
                  <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Plus className="w-5 h-5 text-blue-400" />
                    Ajouter un fond
                  </h2>

                  {/* Preview */}
                  {previewUrl && (
                    <div className="mb-4 rounded-xl overflow-hidden border border-blue-500/20 relative max-h-48">
                      <button onClick={() => { setSelectedFile(null); setPreviewUrl(null); }}
                        className="absolute top-2 right-2 p-1 bg-red-500/80 rounded-full text-white hover:bg-red-600 z-10">
                        <X className="w-4 h-4" />
                      </button>
                      {selectedFile?.type.startsWith('video/') ? (
                        <video src={previewUrl} className="w-full max-h-48 object-cover" autoPlay muted loop />
                      ) : (
                        <img src={previewUrl} className="w-full max-h-48 object-cover" alt="Aperçu" />
                      )}
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div>
                      <label className="text-sm text-blue-300/70 mb-1 block">Durée (secondes)</label>
                      <input
                        type="number"
                        min="3"
                        max="120"
                        value={newDuration}
                        onChange={(e) => setNewDuration(parseInt(e.target.value) || 10)}
                        data-testid="new-bg-duration"
                        className="w-full bg-slate-900/50 border border-blue-500/20 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-blue-400"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-sm text-blue-300/70 mb-1 block">Lien cliquable (optionnel)</label>
                      <input
                        type="url"
                        value={newLink}
                        onChange={(e) => setNewLink(e.target.value)}
                        placeholder="https://monsite.fr"
                        data-testid="new-bg-link"
                        className="w-full bg-slate-900/50 border border-blue-500/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/30 text-sm focus:outline-none focus:border-blue-400"
                      />
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <input ref={fileInputRef} type="file" accept="image/*,video/*" onChange={handleFileSelect} className="hidden" />
                    <Button onClick={() => fileInputRef.current?.click()}
                      variant="outline" className="border-blue-500/30 text-blue-300 hover:bg-blue-500/10">
                      <Image className="w-4 h-4 mr-2" />
                      Choisir un fichier
                    </Button>
                    {selectedFile && (
                      <Button onClick={uploadBackground} disabled={uploading}
                        className="bg-blue-600 hover:bg-blue-700 text-white">
                        {uploading ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{Math.round(uploadProgress)}%</>
                        ) : (
                          <><Upload className="w-4 h-4 mr-2" />Ajouter le fond</>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* Info */}
              <div className="bg-slate-800/30 border border-blue-500/10 rounded-xl p-4 text-sm text-blue-300/50">
                <p>Les fonds sont affichés sur vos pages de téléchargement :</p>
                {randomOrder ? (
                  <p className="mt-1"><Shuffle className="w-3 h-3 inline mr-1" />Ordre aléatoire activé — les fonds seront mélangés à chaque visite.</p>
                ) : (
                  <p className="mt-1">fond 1 ({backgrounds[0]?.duration || '...'}s) → fond 2 ({backgrounds[1]?.duration || '...'}s) → fond 3 ({backgrounds[2]?.duration || '...'}s) → etc.</p>
                )}
                <p className="mt-1">Si un lien est défini, le visiteur peut cliquer sur le fond pour l'ouvrir.</p>
              </div>
            </div>
          )}
        </div>
      </main>
      <DynamicFooter />
    </div>
  );
};

export default UserBackgroundManager;
